import { useQuery, type QueryClient } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, retainSearchParams, useLocation } from "@tanstack/react-router";

import { AdminViewContext } from "@/adminView";
import { queries, setAdminView } from "@/api";
import { AdminViewBanner } from "@/components/AdminView";
import { ErrorNote } from "@/components/ErrorNote";
import { LeaseBadge } from "@/components/LeaseBadge";
import { JoinBox } from "@/components/JoinBox";
import { AirframeOnThisPc } from "@/components/OnThisPc";
import { InvitePilot } from "@/components/ShareAirframe";
import { SignInNeeded } from "@/components/SignInNeeded";
import { visibilityWord } from "@/lib/format";

type AirframeSearch = { admin?: true };

/**
 * The record of an airframe reads differently with the administrator's view
 * on (decision 131): what was read of this airframe the other way round is
 * forgotten when the view turns on or off (every query keyed on its id), so
 * no page shows a member's record to the pilot the administrator is outside
 * the view, nor a stranger's to the view.
 */
function forgetTheRecord(queryClient: QueryClient, id: string) {
  queryClient.removeQueries({ predicate: (q) => q.queryKey[1] === id });
}

export const Route = createFileRoute("/airframes/$id")({
  // The administrator's view (decision 131) rides in the address as `?admin`,
  // put there by the admin page's links alone and kept across the sections;
  // the platform is asked for it only while the address says so, and the
  // page left turns it off: elsewhere an administrator is the pilot they are.
  // A hover's preload is not a visit: it changes nothing.
  validateSearch: (search: Record<string, unknown>): AirframeSearch =>
    search.admin === true || search.admin === "true" || search.admin === 1 || search.admin === "1" ? { admin: true } : {},
  search: { middlewares: [retainSearchParams(["admin"])] },
  beforeLoad: ({ search, params, context, preload }) => {
    if (preload) return;
    if (setAdminView(search.admin === true)) forgetTheRecord(context.queryClient, params.id);
  },
  onLeave: (match) => {
    if (setAdminView(false)) forgetTheRecord((match.context as { queryClient: QueryClient }).queryClient, (match.params as { id: string }).id);
  },
  component: AirframeLayout,
});

/** The airframe's page (F413, decision 055): the registration, the chips, the facts, the sections as tabs (History since F478, decision 099). */
function AirframeLayout() {
  const { id } = Route.useParams();
  const { admin } = Route.useSearch();
  // A flight of the logbook keeps the Logbook tab lit (F417).
  const onFlight = useLocation({ select: (l) => l.pathname.includes("/flights/") });
  const me = useQuery(queries.me());
  // The airframes are for the pilots (F415): a visitor is asked to sign in, not shown an error.
  const airframe = useQuery({ ...queries.airframe(id), enabled: !!me.data });
  if (me.isPending) return null;
  if (me.error) return <ErrorNote error={me.error} />;
  if (!me.data) return <SignInNeeded what="this airframe" />;
  if (airframe.error) return <ErrorNote error={airframe.error} />;
  if (!airframe.data) return <p className="muted">Loading the airframe...</p>;
  const a = airframe.data;
  const manages = a.your_role === "owner" || a.your_role === "captain";
  // The administrator's view (decision 131): the address asked for it and the reader administers.
  const adminView = admin === true && !!me.data.user.is_admin;
  const canRead = !!a.your_role || adminView;
  const facts = [
    "Challenger 650",
    `MSN ${a.identity.msn}`,
    a.identity.selcal ? `SELCAL ${a.identity.selcal}` : null,
    a.identity.livery || "Default livery",
    `aircraft ${a.identity.cl650_version}`,
    a.your_role === "owner" ? "owned by you" : `owned by ${a.owner.name}`,
    a.location ? `at ${a.location.icao ?? ""}${a.location.icao && a.location.name ? " " : ""}${a.location.name ?? (a.location.icao ? "" : "a field off the airports")}`.trim() : null,
    a.members ? `${a.members.length} member${a.members.length === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return (
    <AdminViewContext.Provider value={adminView}>
      <div className="space-y-5">
        {adminView ? <AdminViewBanner airframe={a} /> : null}
      <header className="flex flex-wrap items-start gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[2rem] font-bold leading-none tracking-tight" data-testid="reg">
              {a.identity.reg}
            </h1>
            {a.name ? (
              <span className="muted text-lg" data-testid="name">
                {a.name}
              </span>
            ) : (
              <span className="muted text-lg">No name yet</span>
            )}
            <LeaseBadge lease={a.lease} />
            {a.your_role ? <span className="pill pill-ok">you: {a.your_role}</span> : null}
            {a.unlisted_at ? (
              <span className="pill pill-warn" data-testid="unlisted-pill" title="Off ShariFly: only you see it and nobody books it. Put it back from Settings.">
                unlisted
              </span>
            ) : null}
            <span className="pill" data-testid="visibility-pill">
              {visibilityWord(a.visibility)}
            </span>
          </div>
          <p className="muted text-[0.9286rem]">{facts.join(" · ")}</p>
        </div>
        <span className="flex-1" />
        {manages ? <InvitePilot airframeId={a.id} reg={a.identity.reg} /> : null}
        {a.photo_url ? <img src={a.photo_url} alt="" className="h-16 w-28 rounded-lg object-cover" /> : null}
      </header>
      {!a.your_role && !adminView ? <JoinBox airframe={a} /> : null}
      {adminView && !a.your_role ? null : <AirframeOnThisPc airframeId={a.id} />}
      <nav className="tabs" aria-label="Sections">
        <Link to="/airframes/$id" params={{ id }} className="tab" activeOptions={{ exact: true }} activeProps={{ className: "tab tab-active" }}>
          Overview
        </Link>
        {canRead ? (
          <Link
            to="/airframes/$id/ledger"
            params={{ id }}
            className={`tab ${onFlight ? "tab-active" : ""}`}
            activeProps={{ className: "tab tab-active" }}
            data-testid="tab-logbook"
          >
            Logbook
          </Link>
        ) : null}
        {canRead ? (
          <Link to="/airframes/$id/maintenance" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }} data-testid="tab-maintenance">
            Maintenance
          </Link>
        ) : null}
        {airframe.data?.your_role ? (
          <Link to="/airframes/$id/preflight" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }} data-testid="tab-preflight">
            Pre-flight
          </Link>
        ) : null}
        {canRead ? (
          <Link to="/airframes/$id/schedule" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }} data-testid="tab-schedule">
            Schedule
          </Link>
        ) : null}
        <Link to="/airframes/$id/members" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }}>
          Members
        </Link>
        {canRead ? (
          <Link to="/airframes/$id/history" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }} data-testid="tab-history">
            History
          </Link>
        ) : null}
        {manages ? (
          <Link to="/airframes/$id/settings" params={{ id }} className="tab" activeProps={{ className: "tab tab-active" }}>
            Settings
          </Link>
        ) : null}
      </nav>
      <Outlet />
      </div>
    </AdminViewContext.Provider>
  );
}
